// The Wodi terminology CSV format, `wodi-terminology-csv-v1`
// (docs/plans/phase1-terminology-and-import.md §4.3; spec in
// docs/integrations/terminology/wodi-terminology-csv.md).
//
// This format is ours, so it is fully specified. Official release files (WHO ICD-11,
// LOINC, KNHTS) are mapped to it by separate format profiles, each written only from a
// real sample file (TODO(verify-spec)). One import is exactly one code system version:
// the system and version come from the import's parameters, never from the rows.

import { list, oneOf, text, withDefault, yesNo } from '../csv/cells.ts';
import { readCells } from '../csv/rows.ts';
import type { ColumnSpec, RowCells, RowCheck, RowIssue } from '../csv/rows.ts';
import { codeSearchKey } from './search.ts';
import type { CodeMatch } from './search.ts';

export const WODI_TERMINOLOGY_FORMAT = 'wodi-terminology-csv-v1';

export type TerminologyColumn =
  'code' | 'display' | 'status' | 'selectable' | 'parent_code' | 'synonyms' | 'index_terms';

export const TERMINOLOGY_COLUMNS: readonly (ColumnSpec<TerminologyColumn> & {
  readonly description: string;
})[] = [
  { key: 'code', required: true, description: 'The code, 1 to 64 characters, unique in the file.' },
  { key: 'display', required: true, description: 'The preferred title, up to 500 characters.' },
  { key: 'status', required: false, description: 'active (default) or inactive.' },
  {
    key: 'selectable',
    required: false,
    description: 'yes (default) or no. No for grouping entries such as chapters.',
  },
  {
    key: 'parent_code',
    required: false,
    description: 'The code of the parent entry, which must be in the same file.',
  },
  { key: 'synonyms', required: false, description: 'Other names, separated by |.' },
  { key: 'index_terms', required: false, description: 'Index terms, separated by |.' },
];

export const MAX_CODE_LENGTH = 64;
export const MAX_TERM_LENGTH = 500;
const MAX_TERMS = 200;

export interface TerminologyRow {
  code: string;
  display: string;
  status: 'active' | 'inactive';
  selectable: boolean;
  parentCode: string | null;
  synonyms: string[];
  indexTerms: string[];
}

export type TerminologyRowIssueCode =
  | 'terminology.code_has_spaces'
  | 'terminology.synonym_repeats_display'
  | 'terminology.parent_is_self'
  | 'terminology.parent_not_in_file';

export const TERMINOLOGY_ROW_MESSAGES: Record<TerminologyRowIssueCode, string> = {
  'terminology.code_has_spaces': 'A code cannot contain spaces',
  'terminology.synonym_repeats_display': 'A synonym repeated the title and was left out',
  'terminology.parent_is_self': 'An entry cannot be its own parent',
  'terminology.parent_not_in_file': 'The parent code is not in this file',
};

const readers = {
  code: ['code', text({ maxLength: MAX_CODE_LENGTH })],
  display: ['display', text({ maxLength: MAX_TERM_LENGTH })],
  status: ['status', withDefault(oneOf(['active', 'inactive'] as const), 'active')],
  selectable: ['selectable', withDefault(yesNo(), true)],
  parentCode: ['parent_code', withDefault(text({ maxLength: MAX_CODE_LENGTH }), '')],
  synonyms: [
    'synonyms',
    list({ separator: '|', maxItems: MAX_TERMS, maxItemLength: MAX_TERM_LENGTH }),
  ],
  indexTerms: [
    'index_terms',
    list({ separator: '|', maxItems: MAX_TERMS, maxItemLength: MAX_TERM_LENGTH }),
  ],
} as const;

/**
 * Validates one row of the format. Checks that need other rows (duplicate codes, parents
 * present in the file) are done by the caller with `terminologyDedupeKey` and
 * `rowsWithMissingParents`.
 */
export function validateTerminologyRow(
  cells: RowCells<TerminologyColumn>,
): RowCheck<TerminologyRow> {
  const read = readCells<
    TerminologyColumn,
    Omit<TerminologyRow, 'parentCode'> & { parentCode: string }
  >(cells, readers);
  const issues: RowIssue[] = [...read.issues];
  if (read.value === null) return { value: null, issues };
  const row = read.value;

  if (/\s/.test(row.code)) {
    issues.push({ column: 'code', code: 'terminology.code_has_spaces', severity: 'error' });
  }
  const parentCode = row.parentCode === '' ? null : row.parentCode;
  if (parentCode !== null && parentCode === row.code) {
    issues.push({ column: 'parent_code', code: 'terminology.parent_is_self', severity: 'error' });
  }
  const displayKey = row.display.toLowerCase();
  const synonyms = row.synonyms.filter((s) => s.toLowerCase() !== displayKey);
  if (synonyms.length !== row.synonyms.length) {
    issues.push({
      column: 'synonyms',
      code: 'terminology.synonym_repeats_display',
      severity: 'warning',
    });
  }
  if (issues.some((i) => i.severity === 'error')) return { value: null, issues };
  return { value: { ...row, parentCode, synonyms }, issues };
}

/** The key two rows must not share: the code as the code system compares codes. */
export function terminologyDedupeKey(code: string, match: CodeMatch): string {
  return codeSearchKey(code, match);
}

/**
 * Parent codes that don't appear in the file, by row. `rows` are `[rowNumber, code,
 * parentCode]`; returns the row numbers whose parent is missing, in order.
 */
export function rowsWithMissingParents(
  rows: readonly (readonly [number, string, string | null])[],
  match: CodeMatch,
): number[] {
  const codes = new Set(rows.map(([, code]) => codeSearchKey(code, match)));
  return rows
    .filter(([, , parent]) => parent !== null && !codes.has(codeSearchKey(parent, match)))
    .map(([rowNumber]) => rowNumber);
}
